import React, { useState, useEffect } from 'react';
import {
  X,
  Camera,
  Mic,
  MapPin,
  Bell,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  RotateCw,
  ExternalLink,
  Smartphone,
  RefreshCw,
  ChevronRight,
  Info,
  Sliders,
  Lock,
  EyeOff,
  UserCheck
} from 'lucide-react';
import {
  AppPermissionsStatus,
  checkLivePermissions,
  getStoredPermissions,
  requestCameraPermission,
  requestMicrophonePermission,
  requestLocationPermission,
  requestNotificationPermission,
  resetPermissionOnboarding,
} from '../../lib/permissions';

interface AppPermissionsManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestartOnboarding?: () => void;
}

export const AppPermissionsManagerModal: React.FC<AppPermissionsManagerModalProps> = ({
  isOpen,
  onClose,
  onRestartOnboarding,
}) => {
  const [permissions, setPermissions] = useState<AppPermissionsStatus>(getStoredPermissions());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeRequest, setActiveRequest] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'status' | 'privacy' | 'help'>('status');

  const refreshLiveState = async () => {
    setIsRefreshing(true);
    const updated = await checkLivePermissions();
    setPermissions(updated);
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (isOpen) {
      refreshLiveState();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleRequest = async (type: 'camera' | 'microphone' | 'location' | 'notification') => {
    setActiveRequest(type);
    if (type === 'camera') await requestCameraPermission();
    if (type === 'microphone') await requestMicrophonePermission();
    if (type === 'location') await requestLocationPermission();
    if (type === 'notification') await requestNotificationPermission();
    await refreshLiveState();
    setActiveRequest(null);
  };

  const handleResetFlow = () => {
    resetPermissionOnboarding();
    onClose();
    if (onRestartOnboarding) {
      onRestartOnboarding();
    }
  };

  const getBadge = (state: string) => {
    if (state === 'granted') {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400 bg-emerald-950/80 px-2.5 py-0.5 rounded-full border border-emerald-800">
          <CheckCircle2 className="w-3 h-3" /> Granted
        </span>
      );
    }
    if (state === 'denied') {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-400 bg-rose-950/80 px-2.5 py-0.5 rounded-full border border-rose-800">
          <X className="w-3 h-3" /> Blocked / Denied
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-300 bg-amber-950/80 px-2.5 py-0.5 rounded-full border border-amber-800">
        <AlertTriangle className="w-3 h-3" /> Not Granted (On-Demand)
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black text-white">Privacy & Permission Center</h2>
              <p className="text-[11px] text-slate-400">Zero-Surveillance & Just-In-Time Consent Controls</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-full hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Privacy Pledge Banner */}
        <div className="bg-emerald-950/40 border-b border-emerald-900/50 px-5 py-3 flex items-start gap-2.5 shrink-0">
          <Lock className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-emerald-200/90 leading-relaxed">
            <strong className="text-emerald-300 font-bold">Privacy Guarantee:</strong> No location, camera, microphone, or personal information is ever accessed without your explicit consent. Permissions are requested only when required for specific actions.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex border-b border-slate-800 bg-slate-950/50 px-5 pt-2 gap-4 text-xs font-bold shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('status')}
            className={`pb-2.5 cursor-pointer border-b-2 transition-all ${
              activeTab === 'status'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Permission Status
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('privacy')}
            className={`pb-2.5 cursor-pointer border-b-2 transition-all ${
              activeTab === 'privacy'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Privacy Principles
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('help')}
            className={`pb-2.5 cursor-pointer border-b-2 transition-all ${
              activeTab === 'help'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Device Settings Guide
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto space-y-4 scrollbar-thin">
          {activeTab === 'status' ? (
            <div className="space-y-4">
              {/* Refresh header */}
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>Real-time System Scan</span>
                <button
                  type="button"
                  onClick={refreshLiveState}
                  disabled={isRefreshing}
                  className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 font-semibold cursor-pointer"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  <span>Re-check OS Status</span>
                </button>
              </div>

              {/* 1. Camera */}
              <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center text-pink-400 shrink-0 mt-0.5">
                    <Camera className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <strong className="text-xs font-bold text-white">Camera Access</strong>
                      {getBadge(permissions.camera.state)}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Requested only when you take a KYC verification selfie or scan ID proof. Never accessed in the background.
                    </p>
                  </div>
                </div>
                {permissions.camera.state !== 'granted' && (
                  <button
                    type="button"
                    onClick={() => handleRequest('camera')}
                    disabled={activeRequest === 'camera'}
                    className="self-end sm:self-center px-3 py-1.5 rounded-xl bg-pink-600 hover:bg-pink-500 text-white font-bold text-xs shrink-0 cursor-pointer shadow-sm transition-all"
                  >
                    {activeRequest === 'camera' ? 'Requesting...' : 'Grant Access'}
                  </button>
                )}
              </div>

              {/* 2. Microphone */}
              <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0 mt-0.5">
                    <Mic className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <strong className="text-xs font-bold text-white">Microphone Access</strong>
                      {getBadge(permissions.microphone.state)}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Requested only if you choose to record an audio note or start a safety call. Zero passive listening.
                    </p>
                  </div>
                </div>
                {permissions.microphone.state !== 'granted' && (
                  <button
                    type="button"
                    onClick={() => handleRequest('microphone')}
                    disabled={activeRequest === 'microphone'}
                    className="self-end sm:self-center px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs shrink-0 cursor-pointer shadow-sm transition-all"
                  >
                    {activeRequest === 'microphone' ? 'Requesting...' : 'Grant Access'}
                  </button>
                )}
              </div>

              {/* 3. Location */}
              <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <strong className="text-xs font-bold text-white">Location Access</strong>
                      {getBadge(permissions.location.state)}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Requested only when you tap &quot;Near Me&quot; or search nearby companions. Never tracked continuously.
                    </p>
                  </div>
                </div>
                {permissions.location.state !== 'granted' && (
                  <button
                    type="button"
                    onClick={() => handleRequest('location')}
                    disabled={activeRequest === 'location'}
                    className="self-end sm:self-center px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shrink-0 cursor-pointer shadow-sm transition-all"
                  >
                    {activeRequest === 'location' ? 'Requesting...' : 'Grant Access'}
                  </button>
                )}
              </div>

              {/* 4. Notifications */}
              <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-400 shrink-0 mt-0.5">
                    <Bell className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <strong className="text-xs font-bold text-white">Push Notifications</strong>
                      {getBadge(permissions.notification.state)}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Used for critical booking confirmations, check-in completion OTPs, and security alerts. No advertising spam.
                    </p>
                  </div>
                </div>
                {permissions.notification.state !== 'granted' && (
                  <button
                    type="button"
                    onClick={() => handleRequest('notification')}
                    disabled={activeRequest === 'notification'}
                    className="self-end sm:self-center px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shrink-0 cursor-pointer shadow-sm transition-all"
                  >
                    {activeRequest === 'notification' ? 'Requesting...' : 'Grant Access'}
                  </button>
                )}
              </div>
            </div>
          ) : activeTab === 'privacy' ? (
            /* Privacy & Data Protection Principles */
            <div className="space-y-3.5 text-xs text-slate-300">
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                <h4 className="font-bold text-white text-sm flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  1. Zero-Surveillance Architecture
                </h4>
                <p className="text-slate-400 text-[11px] leading-relaxed">
                  PairUpToday is engineered to respect your personal privacy. We never monitor your device, record audio or video in the background, or inspect files you have not chosen to upload.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                <h4 className="font-bold text-white text-sm flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-purple-400" />
                  2. No Third-Party Data Selling
                </h4>
                <p className="text-slate-400 text-[11px] leading-relaxed">
                  Your identity documents, telephone numbers, and private meetup details are stored with military-grade encryption and are never sold to ad brokers or third-party marketers.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                <h4 className="font-bold text-white text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4 text-pink-400" />
                  3. Just-In-Time Consent
                </h4>
                <p className="text-slate-400 text-[11px] leading-relaxed">
                  Permissions are never requested upfront upon opening the app. You only see a permission prompt when you intentionally tap a feature that requires it (e.g. taking a KYC selfie or calculating meetup distance).
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                <h4 className="font-bold text-white text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  4. Right to Revoke Anytime
                </h4>
                <p className="text-slate-400 text-[11px] leading-relaxed">
                  You can revoke permissions at any time via your browser or phone operating system settings without losing the ability to browse companions and read reviews.
                </p>
              </div>
            </div>
          ) : (
            /* Help / OS Settings Guide */
            <div className="space-y-4 text-xs text-slate-300">
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                <div className="flex items-center gap-2 text-pink-400 font-bold">
                  <Smartphone className="w-4 h-4" />
                  <span>How to manage permissions on Android</span>
                </div>
                <ol className="list-decimal list-inside space-y-1.5 text-slate-400 text-[11px] pl-1 leading-relaxed">
                  <li>In Browser: Tap the <strong>Padlock / Site Info</strong> icon in the address bar &gt; <strong>Permissions</strong>.</li>
                  <li>In Android App: Open phone <strong>Settings &gt; Apps &gt; PairUpToday &gt; Permissions</strong>.</li>
                  <li>Toggle Camera, Location, or Notifications according to your comfort.</li>
                  <li>Return here and tap <strong>Re-check OS Status</strong>.</li>
                </ol>
              </div>

              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                <div className="flex items-center gap-2 text-purple-400 font-bold">
                  <Smartphone className="w-4 h-4" />
                  <span>How to manage permissions on iPhone / iPad (iOS)</span>
                </div>
                <ol className="list-decimal list-inside space-y-1.5 text-slate-400 text-[11px] pl-1 leading-relaxed">
                  <li>In Safari: Tap the <strong>“aA”</strong> icon in the address bar &gt; <strong>Website Settings</strong>.</li>
                  <li>In iOS App: Open phone <strong>Settings &gt; PairUpToday</strong>.</li>
                  <li>Toggle Camera, Location, or Notifications as desired.</li>
                  <li>Return to the app to continue browsing safely.</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-950/60 flex items-center justify-between shrink-0">
          <span className="text-[11px] text-slate-500">100% User-Consent Controlled</span>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs transition-colors cursor-pointer"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
